import React from 'react';
import { ButtonSize } from '../../types';
import { useTheme } from '../../theme';

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  size?: ButtonSize;
  variant?: 'default' | 'primary' | 'danger';
  tooltip?: string;
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-2.5',
};

type IconVariant = 'default' | 'primary' | 'danger';

const getIconVariantStyles = (
  variant: IconVariant,
  branding: { primaryColor: string; secondaryColor: string; secondaryColorHover: string; textColorMuted: string }
): React.CSSProperties => {
  switch (variant) {
    case 'primary':
      return {
        color: branding.primaryColor,
        backgroundColor: branding.secondaryColor,
      };
    case 'danger':
      return {
        color: '#ea4335',
        backgroundColor: 'transparent',
      };
    default:
      return {
        color: branding.textColorMuted,
        backgroundColor: 'transparent',
      };
  }
};

const getIconHoverStyles = (
  variant: IconVariant,
  branding: { secondaryColorHover: string }
): React.CSSProperties => {
  switch (variant) {
    case 'primary':
      return { backgroundColor: branding.secondaryColorHover };
    case 'danger':
      return { backgroundColor: '#fce8e6' };
    default:
      return { backgroundColor: '#f1f3f4' };
  }
};

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'md',
  variant = 'default',
  tooltip,
  className = '',
  disabled,
  style,
  ...props
}) => {
  const { branding } = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);

  const variantStyle = getIconVariantStyles(variant, branding);
  const hoverStyle = isHovered && !disabled ? getIconHoverStyles(variant, branding) : {};

  return (
    <button
      className={`rounded-full transition-all duration-200 cursor-pointer outline-none border-none disabled:opacity-50 disabled:cursor-not-allowed select-none ${sizeStyles[size]} ${className}`}
      disabled={disabled}
      title={tooltip}
      style={{ ...variantStyle, ...hoverStyle, ...style }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {icon}
    </button>
  );
};

export default IconButton;
